import { warning } from '../../../components/Alert/Alert';
import { MaterialType, isConcrete } from '../../enum/Material';
import { ISubSectorResponse } from '../api/IApiResponse';
import { IFormikFormSubsectors } from '../components/InputForm/Materials/ModalSubsector/FormikSubsector';
import { Shape } from '../enum/HydraulicData';
import { ChannelType, SubSectorPosition } from '../enum/Sector';
import { IProjectMacra1 } from '../store/project/projectSlice';
import i18n from '../../localization';

export const getChannelTypeById = (id: number): ChannelType => {
    switch (id) {
        case 1:
        case 2:
            return ChannelType.LeftOverbank;
        case 3:
        case 4:
        case 5:
            return ChannelType.MainChannel;
        case 6:
        case 7:
            return ChannelType.RightOverbank;
        default:
            return 0;
    }
};

export const getEmptySubsector = (position: SubSectorPosition, sector: IFormikFormSubsectors): ISubSectorResponse => {
    if (SubSectorPosition.Right === position) return { position, length: sector.length, material: sector.material };
    return { position, length: 0, material: sector.material };
};

export const validateProjectBeforeRun = (project: IProjectMacra1): boolean => {
    let initialValue = 0;
    let leftSectorHeight = project.geometry!.reduce((accumulator, currentValue) => {
        if (currentValue.channel < 5) return accumulator + currentValue.height;
        return accumulator;
    }, initialValue);
    let rightSectorHeight = project.geometry!.reduce((accumulator, currentValue) => {
        if (currentValue.channel > 4) return accumulator + currentValue.height;
        return accumulator;
    }, initialValue);
    let maxWater = Math.min(leftSectorHeight, rightSectorHeight);
    if (maxWater <= 0) {
        warning('', `${i18n.t('validation_geo')}`);
        return false;
    }
    let noMats = project.geometry!.filter(
        sec => sec.width > 0 && !!sec.material && sec.material.physicalData && !!sec.material.physicalData[0].roughness && sec.material.physicalData[0].roughness < 0,
    );
    if (noMats.length > 0) {
        let channels: string = '';
        noMats.forEach((x, index) => {
            if (index === 0) channels = x.channel!.toString();
            else channels = channels.concat(', ', x.channel!.toString());
        });
        warning('', `${i18n.t('validation_no_mat', { channel: channels })}`);
        return false;
    }
    if (project.hydraulicData?.shape === Shape.Curved) {
        let fourSect = project.geometry!.find(sect => sect.channel === 4);
        if (!!fourSect?.angle && fourSect?.angle <= 6 && fourSect.width >= 1) {
            warning('', `${i18n.t('validation_section_four')}`);
            return false;
        }
    }
    if (project.hydraulicData?.freeboard && maxWater < project.hydraulicData.freeboard) {
        warning('', `Freeboard not supported`);
        return false;
    }
    if (project.hydraulicData?.discharge && project.hydraulicData.discharge <= 0) {
        warning('', `${i18n.t('validation_discharge')}`);
        return false;
    }
    let geoStab = project.geometry!.filter(
        sec => sec.angle > 34 && (sec.width > 0 || sec.height > 0) && (sec.material?.type === MaterialType.ProtectionSystems || sec.material?.type === MaterialType.NaturalSoils),
    );
    if (geoStab.length > 0) {
        let channels: string = '';
        geoStab.forEach((x, index) => {
            if (index === 0) channels = x.channel!.toString();
            else channels = channels.concat(', ', x.channel!.toString());
        });
        warning('', `${i18n.t('validation_stability', { channel: channels })}`);
    }
    return true;
};

export const hasConcreteMasonry = (project: IProjectMacra1): boolean =>
    !!project.geometry?.some(
        sector => isConcrete(sector.material?.category) || !!sector.subSectors?.some(sub => isConcrete(sub.material?.category)),
    );
